/**
 * i18n Configuration for TreatX Platform
 * Supports 7 languages: en, tr, de, es, ar, ru, fr
 *
 * Usage:
 * - Import { useTranslation } from 'react-i18next' in components
 * - Use t('key.path') to get translated strings
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import locale files
import en from './locales/en.json';
import tr from './locales/tr.json';
import de from './locales/de.json';
import es from './locales/es.json';
import ar from './locales/ar.json';
import ru from './locales/ru.json';
import fr from './locales/fr.json';

// Supported languages — must match backend SUPPORTED_LANGUAGES.
// These are RESOURCES that exist / may be prepared, NOT the set of
// locales the product UI is currently offered in.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Turkce', direction: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Espanol', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Francais', direction: 'ltr' },
];

export const LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

// TREATX-LOCALE-ENABLEMENT-01 — canonical authority for which locales
// are currently offered as PRODUCT UI selections. This is the ONLY
// source of truth consumed by user-facing language selectors (Header,
// GlobalLanguageSwitcher, mobile menu, patient/provider shells).
// A locale being "supported" means the resource exists; a locale
// being "UI-enabled" means the translation coverage has been QA-
// approved and the locale is safe to offer to real users.
//
// To enable Spanish + German after QA passes, edit ONLY this array:
//   ENABLED_UI_LANGUAGE_CODES = ['en', 'tr', 'es', 'de'];
// No component/JSX changes are required — selectors dynamically
// consume this authority via useLanguage().enabledLanguages.
//
// Legal Multilingual Editor is UNAFFECTED — it uses the backend
// `/api/admin/legal/supported-locales` endpoint independently so
// admins can keep drafting future-language legal content before the
// product UI is enabled for those locales.
export const ENABLED_UI_LANGUAGE_CODES = ['en', 'tr'];

export const ENABLED_UI_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (l) => ENABLED_UI_LANGUAGE_CODES.includes(l.code)
);

// Safe fallback resolver — clamps any code (including a stored
// preference for a locale that has since been disabled) to the
// nearest UI-enabled locale. Guarantees the app never renders a
// half-translated shell.
export const resolveEnabledLocale = (code) => {
  if (code && ENABLED_UI_LANGUAGE_CODES.includes(code)) return code;
  return ENABLED_UI_LANGUAGE_CODES[0] || 'en';
};

// TREATX-FIPO-F8_2_C (D-B21) — resolve initial language.
// Precedence:
//   1. Persisted localStorage['i18nextLng'] — set the first time the
//      user explicitly changes language OR after the server-persisted
//      preferred_language is synced via LanguageContext.
//   2. Browser navigator.language / navigator.languages, bounded to
//      the SSOT whitelist. Region forms like `tr-TR` reduce to `tr`.
//   3. Default 'en'.
// After step 1 is populated at least once, browser language will
// never override it again (persisted always wins — D-B21 invariant).
const detectBrowserLanguage = () => {
  try {
    if (typeof navigator === 'undefined') return null;
    const candidates = [];
    if (Array.isArray(navigator.languages)) {
      candidates.push(...navigator.languages);
    }
    if (navigator.language) candidates.push(navigator.language);
    for (const raw of candidates) {
      if (!raw) continue;
      const primary = String(raw).split('-')[0].toLowerCase();
      if (LANGUAGE_CODES.includes(primary)) return primary;
    }
  } catch (e) {
    // navigator not available (SSR / test)
  }
  return null;
};

const getInitialLanguage = () => {
  try {
    const stored = localStorage.getItem('i18nextLng');
    if (stored && LANGUAGE_CODES.includes(stored)) {
      // TREATX-LOCALE-ENABLEMENT-01 — if the persisted preference is
      // for a locale that is NOT currently UI-enabled (e.g. user
      // previously picked `de` and DE has since been disabled), fall
      // back safely so the app never renders half-translated.
      return resolveEnabledLocale(stored);
    }
  } catch (e) {
    // localStorage not available
  }
  const detected = detectBrowserLanguage();
  if (detected) return resolveEnabledLocale(detected);
  return ENABLED_UI_LANGUAGE_CODES[0] || 'en';
};

const initialLanguage = getInitialLanguage();

// Initialize i18n
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      tr: { translation: tr },
      de: { translation: de },
      es: { translation: es },
      ar: { translation: ar },
      ru: { translation: ru },
      fr: { translation: fr },
    },
    lng: initialLanguage,
    fallbackLng: 'en',
    defaultNS: 'translation',
    debug: false,
    interpolation: {
      escapeValue: false // React already escapes values
    }
  });

// Helper function to change language.
// TREATX-LOCALE-ENABLEMENT-01 — accepts any supported code but
// resolves to an ENABLED locale before applying. This keeps legal-
// admin / test paths that legitimately want to preview a resource
// working (they call i18n.changeLanguage directly if they need to
// bypass the enabled-list clamp for scoped previews).
export const changeLanguage = (langCode) => {
  const resolved = resolveEnabledLocale(langCode);
  if (LANGUAGE_CODES.includes(resolved)) {
    i18n.changeLanguage(resolved);
    localStorage.setItem('i18nextLng', resolved);
    // Set document direction for RTL languages
    const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === resolved);
    document.documentElement.dir = langInfo?.direction || 'ltr';
    document.documentElement.lang = resolved;
  }
};

// Initialize direction on load
const initLangInfo = SUPPORTED_LANGUAGES.find(l => l.code === initialLanguage);
if (initLangInfo) {
  document.documentElement.dir = initLangInfo.direction;
  document.documentElement.lang = initialLanguage;
}

export default i18n;
